// Stable hash-based IDs for statements and batches. PRD § 2.6 — statement IDs
// are `sha1(sha1(text) + startOffset).slice(0, 12)`.
//
// SHA1 is implemented inline (pure JS) so this module runs in both Node and
// the Electron renderer (where `node:crypto` is unavailable under
// `contextIsolation: true` / `nodeIntegration: false`).

const ENCODER = new TextEncoder();

function rotl32(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0;
}

function sha1Hex(input: string): string {
  const msg = ENCODER.encode(input);
  const msgLen = msg.length;
  const bitLen = msgLen * 8;
  const padded = new Uint8Array(Math.ceil((msgLen + 9) / 64) * 64);
  padded.set(msg);
  padded[msgLen] = 0x80;
  const view = new DataView(padded.buffer);
  // 64-bit big-endian length in bits at the end.
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000), false);
  view.setUint32(padded.length - 4, bitLen >>> 0, false);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const w = new Uint32Array(80);

  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = view.getUint32(chunk + i * 4, false);
    }
    for (let i = 16; i < 80; i++) {
      w[i] = rotl32(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let i = 0; i < 80; i++) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl32(a, 5) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = rotl32(b, 30);
      b = a;
      a = temp;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const toHex = (n: number) => n.toString(16).padStart(8, '0');
  return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4);
}

export function statementId(text: string, startOffset: number): string {
  const contentHash = sha1Hex(text);
  return sha1Hex(`${contentHash}${startOffset}`).slice(0, 12);
}

export function makeBatchId(documentId: string, batchIndex: number, contentStart: number): string {
  return sha1Hex(`${documentId}:${batchIndex}:${contentStart}`).slice(0, 12);
}
